<template>
    <b-modal
        :id="modalId"
        ref="modalRef"
        :title="modalTitle"
        :data-test="modalId"
        size="lg"

        scrollable
        @show="onModalShow"
        @hidden="onModalHidden"
    >
        <!-- Setup Tabs (1. Columns Setup [Default] | 2. Grid Features & Hygiene) -->
        <div
            v-if="hasFeatureConfig"
            class="grid-setup-tabs d-flex align-items-center mb-3 p-1 rounded border"
            role="tablist"
            data-test="grid-setup-tabs"
        >
            <button
                type="button"
                role="tab"
                :aria-selected="activeSetupTab === 'columns' ? 'true' : 'false'"
                class="grid-setup-tab-btn flex-fill d-flex align-items-center justify-content-center py-2 px-3 rounded border-0 font-weight-bold small"
                :class="{ 'is-active': activeSetupTab === 'columns' }"
                data-test="tab-grid-setup-columns"
                @click="setSetupTab('columns')"
            >
                <i class="bi bi-layout-three-columns mr-2"></i>
                <span>Columns Setup</span>
                <span class="badge ml-2 extra-small px-2 py-1 grid-setup-tab-badge">{{ activeColumns.length }}</span>
            </button>
            <button
                type="button"
                role="tab"
                :aria-selected="activeSetupTab === 'features' ? 'true' : 'false'"
                class="grid-setup-tab-btn flex-fill d-flex align-items-center justify-content-center py-2 px-3 rounded border-0 font-weight-bold small ml-1"
                :class="{ 'is-active': activeSetupTab === 'features' }"
                data-test="tab-grid-setup-features"
                @click="setSetupTab('features')"
            >
                <i :class="[featureConfig.icon || 'bi bi-sliders', 'mr-2']"></i>
                <span>{{ featureConfig.tabTitle || featureConfig.title || 'Smart Signals & Hygiene' }}</span>
                <span
                    class="badge ml-2 extra-small px-2 py-1"
                    :class="draftFeatureEnabled ? 'feature-status-badge--on' : 'feature-status-badge--off'"
                >
                    {{ draftFeatureEnabled ? `${enabledFeatureCount}/${draftFeatureItems.length} On` : 'Off' }}
                </span>
            </button>
        </div>

        <!-- Tab 1 (Default): Columns Setup -->
        <div
            v-show="!hasFeatureConfig || activeSetupTab === 'columns'"
            data-test="tab-panel-columns"
        >
        <!-- Modal Subtitle -->
        <div class="mb-3 text-muted small">
            {{ subtitle }}
        </div>

        <!-- Rule Validation Alert Banner -->
        <b-alert
            v-if="highlightFieldName && typeof highlightFieldName === 'string'"
            show
            variant="warning"
            dismissible
            @dismissed="highlightFieldName = ''"
            class="py-2 px-3 mb-3 small d-flex align-items-center"
        >
            <i class="bi bi-exclamation-triangle-fill mr-2 text-warning" style="font-size: 1.1rem;"></i>
            <div>
                Azure DevOps rule validation requires <strong>{{ highlightFieldName }}</strong>.
                <span v-if="selectedFieldToAdd && (selectedFieldToAdd.referenceName === highlightFieldName || selectedFieldToAdd.name === highlightFieldName)">
                    It has been selected in the dropdown below — click <strong>Add Column</strong> and then <strong>Save Columns</strong>.
                </span>
                <span v-else>
                    Please add it to your columns below and click <strong>Save Columns</strong>.
                </span>
            </div>
        </b-alert>



        <!-- 1. Interactive Drag & Drop Live Column Sequence -->
        <div class="column-flow-card p-3 mb-3 bg-light rounded border">
            <div class="d-flex align-items-center justify-content-between mb-2">
                <span class="small font-weight-bold text-uppercase text-secondary">
                    <i class="bi bi-grip-vertical mr-1 text-primary"></i>Live Column Sequence ({{ activeColumns.length }})
                </span>
                <span class="extra-small text-muted">
                    <i class="bi bi-arrows-expand mr-1"></i>Drag pills left / right to reorder
                </span>
            </div>

            <div class="d-flex flex-wrap align-items-center gap-2 live-sequence-container">
                <!-- Reorderable columns (System and Custom except Action) -->
                <div
                    v-for="(col, idx) in reorderableColumns"
                    :key="col.referenceName"
                    class="badge px-3 py-2 flow-pill draggable-pill"
                    :class="[
                        col.isSystem ? 'system-flow-pill' : 'custom-flow-pill',
                        { 'is-dragging': draggedIndex === idx },
                        { 'is-drop-target-left': dragOverIndex === idx && dragOverPos === 'left' },
                        { 'is-drop-target-right': dragOverIndex === idx && dragOverPos === 'right' },
                        { 'flow-pill-hidden': col.hidden }
                    ]"
                    :title="col.isSystem ? `${col.label} (System Column - ${col.hidden ? 'Hidden' : 'Visible'}) - Drag to reorder` : `${col.label} (${col.referenceName}) - ${col.hidden ? 'Hidden' : 'Visible'}${col.workItemTypes && col.workItemTypes.length ? ' - Applies to: ' + col.workItemTypes.join(', ') : ''} - Drag to reorder`"
                    draggable="true"
                    data-test="pill-column"
                    :data-test-id="col.referenceName"
                    @dragstart="onDragStart(idx, $event)"
                    @dragover.prevent="onDragOver(idx, $event)"
                    @dragleave="onDragLeave(idx)"
                    @drop.prevent="onDrop(idx)"
                    @dragend="onDragEnd"
                >
                    <i class="bi bi-grip-vertical mr-1 grip-handle"></i>
                    <span class="pill-order-num mr-1">#{{ idx + 1 }}</span>
                    <span class="pill-label font-weight-bold" :class="{ 'text-decoration-line-through': col.hidden }">{{ col.label }}</span>
                    <span v-if="col.metricSuffix" class="badge badge-light border text-dark ml-1 extra-small">{{ col.metricSuffix }}</span>
                    <b-badge v-if="!col.isSystem && col.workItemTypes && col.workItemTypes.length" variant="light" class="border ml-1 extra-small pill-types-badge" :title="`Applies to: ${col.workItemTypes.join(', ')}`"><i class="bi bi-card-checklist text-primary"></i></b-badge>
                    <b-badge v-if="col.hidden" class="ml-1 extra-small pill-hidden-badge">Hidden</b-badge>

                    <!-- Visibility Toggle (Eye icon) -->
                    <button
                        type="button"
                        class="btn btn-link p-0 flow-pill-action flow-pill-eye ml-2"
                        @click.stop="toggleColumnVisibility(col)"
                        :title="col.hidden ? 'Show column in grid' : 'Hide column from grid'"
                        :aria-label="col.hidden ? 'Show column in grid' : 'Hide column from grid'"
                        data-test="btn-toggle-col-visibility"
                    >
                        <i class="bi" :class="col.hidden ? 'bi-eye-slash-fill flow-pill-eye-hidden' : 'bi-eye-fill flow-pill-eye-visible'"></i>
                    </button>

                    <!-- Value Colors (Palette icon for enumerated columns) -->
                    <button
                        v-if="hasEnumeratedValues(col)"
                        type="button"
                        class="btn btn-link p-0 flow-pill-action flow-pill-palette ml-2"
                        @click.stop="openValueColors(col)"
                        :title="hasCustomColors(col) ? 'Customize colors for values (active)' : 'Assign colors to values'"
                        data-test="btn-col-value-colors"
                    >
                        <i class="bi bi-palette-fill text-warning"></i>
                    </button>

                    <!-- Remove (Custom only) or Lock (System only) -->
                    <span
                        v-if="!col.isSystem"
                        class="flow-pill-remove ml-2"
                        @click.stop="removeColumn(idx)"
                        title="Remove custom column"
                    >&times;</span>
                    <i
                        v-else
                        class="bi bi-lock-fill ml-1 flow-pill-lock"
                        style="font-size: 0.75rem;"
                        title="Core system column cannot be removed, but can be hidden"
                    ></i>
                </div>

                <!-- Action Column Fixed Last -->
                <div
                    v-if="actionColumn"
                    class="badge px-3 py-2 flow-pill locked-action-pill"
                    title="Action column is always the last column in the grid"
                >
                    <span class="pill-order-num mr-1">#{{ reorderableColumns.length + 1 }}</span>
                    <i class="bi bi-lock-fill mr-1 text-warning" style="font-size: 0.75rem;"></i>
                    <span class="pill-label font-weight-bold">{{ actionColumn.label }}</span>
                    <span class="badge ml-2 extra-small action-fixed-badge">Fixed Last</span>
                </div>
            </div>
            <div class="extra-small text-muted mt-2">
                <i class="bi bi-info-circle mr-1"></i>System badges are core columns (cannot be deleted). Click the eye icon to hide or show columns.<span v-if="allowCustomFields"> Blue badges are custom fields.</span>
            </div>
        </div>

        <!-- 2. Autocomplete Dropdown: Add Custom Column -->
        <div v-if="allowCustomFields" class="add-custom-col-card p-3 mb-3 rounded border">
            <label class="small font-weight-bold text-uppercase text-dark mb-2 d-flex align-items-center justify-content-between">
                <span>
                    <i class="bi bi-plus-circle-fill text-primary mr-1"></i>Add Custom Field
                </span>
                <span v-if="discoveredFields.length > 0" class="badge badge-light border text-muted font-weight-normal">
                    {{ availableFieldsToAdd.length }} available to add
                </span>
            </label>
            <div class="d-flex align-items-center">
                <div class="flex-grow-1 mr-2">
                    <vue-multiselect
                        class="custom-fields-autocomplete"
                        v-model="selectedFieldToAdd"
                        :options="availableFieldsToAdd"
                        track-by="referenceName"
                        label="name"
                        :placeholder="loadingDiscovery ? 'Discovering available custom fields from Azure DevOps...' : (availableFieldsToAdd.length ? 'Search by name or reference name (e.g. Activity, Environment)...' : 'No more custom fields available')"
                        :disabled="loadingDiscovery || !availableFieldsToAdd.length"
                        :searchable="true"
                        :close-on-select="true"
                        :show-labels="false"
                        @select="onFieldSelected"
                    >
                        <template #option="{ option }">
                            <div class="d-flex justify-content-between align-items-center py-1 w-100">
                                <div class="mr-2">
                                    <div class="font-weight-bold text-dark d-flex align-items-center flex-wrap">
                                        <span>{{ option.name }}</span>
                                        <b-badge v-if="option.alwaysRequired" variant="danger" class="ml-1" style="font-size: 0.65rem;">Required</b-badge>
                                    </div>
                                    <div class="text-muted extra-small font-monospace">{{ option.referenceName }}</div>
                                    <div v-if="option.workItemTypes && option.workItemTypes.length" class="mt-1 d-flex flex-wrap align-items-center">
                                        <span class="text-muted mr-1" style="font-size: 0.7rem;">Types:</span>
                                        <span v-for="t in option.workItemTypes" :key="t" class="badge badge-light border text-secondary extra-small mr-1 mb-1">
                                            {{ t }}
                                        </span>
                                    </div>
                                    <div v-else class="mt-1">
                                        <span class="badge badge-light border text-muted extra-small">All Types</span>
                                    </div>
                                </div>
                                <b-badge variant="light" class="border text-secondary ml-2 font-weight-normal align-self-start">
                                    {{ option.fieldType || 'String' }}
                                </b-badge>
                            </div>
                        </template>
                        <template #noResult>
                            <span class="text-muted small">No custom fields match your search.</span>
                        </template>
                        <template #noOptions>
                            <span class="text-muted small">{{ discoveredFields.length ? 'All discovered custom fields are currently active in your grid.' : 'No custom fields found.' }}</span>
                        </template>
                    </vue-multiselect>
                </div>
                <b-button
                    variant="primary"
                    size="sm"
                    class="px-3"
                    :disabled="!selectedFieldToAdd"
                    @click="addSelectedColumn"
                >
                    <i class="bi bi-plus-lg mr-1"></i>Add
                </b-button>
            </div>
            <div class="text-muted extra-small mt-2">
                Type to search across custom fields. Once added, you can reposition the badge anywhere in the sequence by dragging it.
            </div>
        </div>

        <!-- 3. Loading State -->
        <div v-if="loadingDiscovery" class="py-4">
            <load-spinner size="sm" text="Scanning Azure DevOps process template for custom fields..." />
        </div>

        <!-- 4. Permission Error Banner -->
        <div v-else-if="isPermissionError" class="p-3 mb-3 rounded border border-warning" style="background-color: #fff9e6;">
            <div class="d-flex align-items-start">
                <i class="bi bi-shield-lock-fill text-warning fa-2x mr-3"></i>
                <div class="flex-grow-1">
                    <h6 class="font-weight-bold text-dark mb-1">
                        Azure DevOps Permissions Required
                    </h6>
                    <div class="small text-muted mb-2">
                        {{ discoveryError }}
                    </div>
                    <div class="small mb-2">
                        <strong>Required Personal Access Token (PAT) Scopes:</strong>
                        <div class="mt-1 d-flex flex-wrap">
                            <span class="badge badge-light border text-dark mr-2 mb-1">
                                <i class="bi bi-check2 text-success mr-1"></i>Work Items: Read &amp; Write
                            </span>
                            <span class="badge badge-light border text-dark mr-2 mb-1">
                                <i class="bi bi-check2 text-success mr-1"></i>Project &amp; Team: Read
                            </span>
                        </div>
                    </div>
                    <div class="d-flex align-items-center mt-2">
                        <b-button size="sm" variant="warning" class="mr-2 font-weight-bold shadow-sm" @click="goToConfig">
                            <i class="bi bi-gear-fill mr-1"></i>Update Connection Settings
                        </b-button>
                        <b-button size="sm" variant="outline-secondary" @click="discoverFields(true)">
                            <i class="bi bi-arrow-clockwise mr-1"></i>Retry
                        </b-button>
                    </div>
                </div>
            </div>
        </div>

        <!-- 5. General Error Banner -->
        <b-alert v-else-if="discoveryError" show variant="danger" class="d-flex align-items-center mb-3">
            <i class="bi bi-exclamation-triangle-fill mr-2 h5 mb-0"></i>
            <div class="flex-grow-1 small">
                <strong>Failed to discover fields:</strong> {{ discoveryError }}
            </div>
            <b-button size="sm" variant="outline-danger" class="ml-2" @click="discoverFields(true)">
                <i class="bi bi-arrow-clockwise mr-1"></i>Retry
            </b-button>
        </b-alert>

        </div>

        <!-- Tab 2: Grid-Specific Feature & Hygiene Setup -->
        <div
            v-if="hasFeatureConfig"
            v-show="activeSetupTab === 'features'"
            data-test="tab-panel-features"
        >
            <div
                class="grid-feature-setup-card p-3 mb-2 rounded border"
                data-test="grid-setup-feature-section"
            >
                <div class="d-flex align-items-start justify-content-between flex-wrap mb-3 pb-2 border-bottom feature-setup-header">
                    <div class="mr-3 flex-grow-1" style="min-width: 200px;">
                        <div class="d-flex align-items-center flex-wrap">
                            <span class="small font-weight-bold text-uppercase feature-setup-title d-inline-flex align-items-center">
                                <i :class="[featureConfig.icon || 'bi bi-sliders', 'mr-2 text-primary']"></i>
                                <span>{{ featureConfig.title || 'Grid Features & Signals' }}</span>
                            </span>
                            <span
                                class="badge ml-2 extra-small px-2 py-1"
                                :class="draftFeatureEnabled ? 'feature-status-badge--on' : 'feature-status-badge--off'"
                                data-test="badge-feature-master-status"
                            >
                                {{ draftFeatureEnabled ? `Enabled (${enabledFeatureCount}/${draftFeatureItems.length})` : 'Disabled' }}
                            </span>
                        </div>
                        <div v-if="featureConfig.subtitle" class="extra-small text-muted mt-1">
                            {{ featureConfig.subtitle }}
                        </div>
                    </div>
                    <div class="d-flex align-items-center mt-2 mt-sm-0">
                        <b-form-checkbox
                            v-model="draftFeatureEnabled"
                            switch
                            class="font-weight-bold small m-0 feature-master-switch"
                            :data-test="featureConfig.masterTestId || 'toggle-feature-master'"
                        >
                            <span class="extra-small font-weight-bold">{{ featureConfig.masterLabel || 'Enable Feature' }}</span>
                        </b-form-checkbox>
                    </div>
                </div>

                <b-row class="feature-cases-grid mx-n1">
                    <b-col
                        v-for="item in draftFeatureItems"
                        :key="item.key"
                        cols="12"
                        md="6"
                        class="px-1 mb-2"
                    >
                        <div
                            class="feature-case-card p-2 rounded border h-100 d-flex flex-column justify-content-between"
                            :class="{
                                'is-master-disabled': !draftFeatureEnabled,
                                'is-case-enabled': draftFeatureEnabled && item.enabled,
                                'is-case-disabled': draftFeatureEnabled && !item.enabled
                            }"
                            :data-test="'card-feature-' + item.key"
                            @click="onFeatureCardClick(item, $event)"
                        >
                            <div class="d-flex align-items-start justify-content-between mb-1">
                                <div class="pr-2">
                                    <div class="small font-weight-bold feature-case-label">
                                        {{ item.label }}
                                    </div>
                                    <span
                                        v-if="item.badgeText || item.previewBadge"
                                        class="feature-preview-pill d-inline-flex align-items-center mt-1"
                                        :class="'feature-preview-pill--' + (item.badgeVariant || item.previewVariant || 'amber')"
                                    >
                                        <i v-if="item.badgeIcon || item.icon" :class="[item.badgeIcon || item.icon, 'mr-1']"></i>
                                        <span>{{ item.badgeText || item.previewBadge }}</span>
                                    </span>
                                </div>
                                <div @click.stop>
                                    <b-form-checkbox
                                        v-model="item.enabled"
                                        switch
                                        :disabled="!draftFeatureEnabled"
                                        class="m-0"
                                        :data-test="item.testId || ('toggle-feature-' + item.key)"
                                        :aria-label="item.label"
                                    ></b-form-checkbox>
                                </div>
                            </div>
                            <div v-if="item.description" class="extra-small text-muted mt-1 feature-case-desc">
                                {{ item.description }}
                            </div>
                        </div>
                    </b-col>
                </b-row>
            </div>
        </div>

        <!-- 6. Nested Sub-Modal: Configure Value Colors -->
        <b-modal
            id="modal-col-value-colors"
            ref="modalValueColorsRef"
            :title="colorModalTitle"
            data-test="modal-col-value-colors"
            size="lg"
    
            scrollable
            modal-class="modal-col-value-colors-dialog"
            @hidden="onColorModalHidden"
        >
            <div class="mb-3 text-muted small d-flex justify-content-between align-items-center">
                <span>Assign colors to choices for <strong>{{ editingColorCol ? editingColorCol.label : '' }}</strong>.</span>
                <div class="d-flex align-items-center">
                    <b-button
                        size="sm"
                        variant="outline-primary"
                        class="mr-2 extra-small py-1 px-2"
                        @click="autoAssignPalette"
                        title="Auto-assign harmonious colors across values"
                        data-test="btn-auto-assign-palette"
                    >
                        <i class="bi bi-magic mr-1"></i>Auto-Assign
                    </b-button>
                    <b-button
                        size="sm"
                        variant="outline-secondary"
                        class="extra-small py-1 px-2"
                        @click="clearAllValueColors"
                        title="Clear all assigned colors"
                        data-test="btn-clear-colors"
                    >
                        <i class="bi bi-x-circle mr-1"></i>Clear All
                    </b-button>
                </div>
            </div>

            <div v-if="!dedupedColorModalValues || !dedupedColorModalValues.length" class="text-center py-4 text-muted small">
                No dropdown values found for this column. Add values below.
            </div>

            <div v-else class="color-values-wrapper">
                <!-- Optional Work Item Type Filter Pills (if System.State and multiple types exist) -->
                <div v-if="isStateColumn && availableWitFilters.length > 1" class="d-flex flex-wrap gap-1 mb-3 wit-tabs-bar">
                    <b-button
                        size="sm"
                        :variant="selectedWitFilter === 'all' ? 'primary' : 'outline-secondary'"
                        class="extra-small py-1 px-2"
                        @click="selectedWitFilter = 'all'"
                    >
                        All ({{ dedupedColorModalValues.length }})
                    </b-button>
                    <b-button
                        v-for="wf in availableWitFilters"
                        :key="'wit-filter-' + wf.name"
                        size="sm"
                        :variant="selectedWitFilter === wf.name ? 'primary' : 'outline-secondary'"
                        class="extra-small py-1 px-2 d-inline-flex align-items-center"
                        @click="selectedWitFilter = wf.name"
                    >
                        <span v-if="getWitIcon(wf.name)" class="mr-1 d-inline-flex wit-tab-icon" v-html="getWitIcon(wf.name)"></span>
                        <span>{{ wf.name }}</span>
                        <b-badge
                            :variant="selectedWitFilter === wf.name ? 'light' : 'secondary'"
                            class="ml-1 border-0 extra-small font-weight-normal"
                        >
                            {{ wf.count }}
                        </b-badge>
                    </b-button>
                </div>

                <!-- Deduped Values List -->
                <div class="color-values-container">
                    <div
                        v-for="item in visibleDedupedValues"
                        :key="item.value"
                        class="d-flex align-items-center justify-content-between p-2 mb-2 rounded border color-value-row"
                    >
                        <!-- Value preview badge + Type usage badges -->
                        <div class="d-flex align-items-center flex-wrap mr-3 flex-grow-1 gap-2" style="min-width: 0;">
                            <span
                                class="badge px-2 py-1 value-preview-badge text-truncate"
                                :style="getValuePreviewStyle(item.value)"
                                :title="item.value"
                            >
                                {{ item.value }}
                            </span>

                            <!-- Types where this state is used -->
                            <div v-if="item.wits && item.wits.length" class="d-flex align-items-center flex-wrap gap-1">
                                <span
                                    v-for="wit in item.wits"
                                    :key="wit"
                                    class="badge border text-secondary extra-small d-inline-flex align-items-center px-1 py-0 font-weight-normal wit-usage-chip"
                                    :title="`Used in ${wit}`"
                                >
                                    <span v-if="getWitIcon(wit)" class="mr-1 d-inline-flex wit-chip-icon" v-html="getWitIcon(wit)"></span>
                                    <span>{{ wit }}</span>
                                </span>
                            </div>
                        </div>

                        <!-- Swatches + Custom Picker -->
                        <div class="d-flex align-items-center flex-nowrap gap-1 ml-auto flex-shrink-0">
                            <button
                                v-for="swatch in presetColors"
                                :key="swatch.hex"
                                type="button"
                                class="color-swatch-btn"
                                :class="{ 'is-selected': isColorSelected(item.value, swatch.hex) }"
                                :style="{ backgroundColor: swatch.hex }"
                                :title="swatch.name"
                                @click="applyValueColor(item.value, swatch.hex)"
                            ></button>

                            <!-- Native color picker for custom hex -->
                            <label class="color-picker-label m-0 ml-1" title="Pick custom color">
                                <input
                                    type="color"
                                    :value="colorModalMapping[item.value] || '#6c757d'"
                                    class="color-picker-native"
                                    @input="applyValueColor(item.value, $event.target.value)"
                                />
                                <i class="bi bi-eyedropper text-secondary"></i>
                            </label>

                            <!-- Clear color for this value -->
                            <button
                                v-if="colorModalMapping[item.value]"
                                type="button"
                                class="btn btn-link btn-sm p-0 ml-1 text-muted color-clear-btn"
                                title="Reset color for this value"
                                @click="clearValueColor(item.value)"
                            >
                                &times;
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Add custom value affordance -->
            <div class="mt-3 pt-2 border-top">
                <label class="extra-small font-weight-bold text-uppercase text-muted mb-1">Add choice value</label>
                <div class="d-flex align-items-center">
                    <b-form-input
                        v-model="newCustomValueInput"
                        size="sm"
                        placeholder="e.g. Blocked, In Review, High..."
                        class="mr-2"
                        @keydown.enter.prevent="addNewCustomValue"
                    ></b-form-input>
                    <b-button
                        size="sm"
                        variant="secondary"
                        :disabled="!newCustomValueInput || !newCustomValueInput.trim()"
                        @click="addNewCustomValue"
                    >
                        <i class="bi bi-plus-lg mr-1"></i>Add
                    </b-button>
                </div>
            </div>

            <template #modal-footer>
                <b-button size="sm" variant="secondary" @click="closeValueColorsModal" data-test="btn-cancel-value-colors">Cancel</b-button>
                <b-button size="sm" variant="primary" @click="saveValueColors" data-test="btn-save-value-colors">
                    <i class="bi bi-check-lg mr-1"></i>Apply Colors
                </b-button>
            </template>
        </b-modal>

        <!-- Modal Footer -->
        <template #modal-footer>
            <b-button
                variant="outline-secondary"
                size="sm"
                @click="resetToDefaultColumns"
                :disabled="saving"
                title="Reset to default core system columns"
                data-test="btn-reset-columns"
            >
                <i class="bi bi-arrow-counterclockwise mr-1"></i>Reset to Default
            </b-button>

            <div>
                <b-button
                    variant="secondary"
                    size="sm"
                    class="mr-2"
                    @click="hideModal"
                    :disabled="saving"
                    data-test="btn-cancel-columns"
                >
                    Cancel
                </b-button>
                <b-button
                    variant="primary"
                    size="sm"
                    @click="saveColumns"
                    :disabled="saving"
                    data-test="btn-save-columns"
                >
                    <b-spinner small v-if="saving" class="mr-1"></b-spinner>
                    <i v-else class="bi bi-check-lg mr-1"></i>Save Columns
                </b-button>
            </div>
        </template>
    </b-modal>
</template>

<script>

import mixinToast from '../mixinToast';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import { resolveWorkItemTypeIcon } from './nav-link-button.vue';

export const STATUS_PRESET_COLORS = [
    { name: 'Emerald', hex: '#10b981' },
    { name: 'Sky Blue', hex: '#0284c7' },
    { name: 'Violet', hex: '#8b5cf6' },
    { name: 'Amber', hex: '#f59e0b' },
    { name: 'Rose', hex: '#ef4444' },
    { name: 'Teal', hex: '#14b8a6' },
    { name: 'Indigo', hex: '#6366f1' },
    { name: 'Pink', hex: '#ec4899' },
    { name: 'Slate', hex: '#64748b' },
    { name: 'Dark', hex: '#334155' }
];

export const DEFAULT_STATE_COLORS = {
    'To Do': '#3b82f6',
    'In Progress': '#10b981',
    'Done': '#8b5cf6',
    'New': '#3b82f6',
    'Active': '#10b981',
    'Resolved': '#8b5cf6',
    'Closed': '#64748b',
    'Removed': '#ef4444',
    'Proposed': '#3b82f6',
    'Design': '#3b82f6',
    'Doing': '#10b981',
    'Committed': '#10b981',
    'Started': '#10b981',
    'Completed': '#8b5cf6',
    'Cut': '#ef4444',
    'Abandoned': '#ef4444'
};

export const DEFAULT_STATE_VALUES = [
    'To Do',
    'In Progress',
    'Done',
    'New',
    'Active',
    'Resolved',
    'Closed',
    'Removed'
];

export function getStoredStateColors() {
    try {
        if (typeof localStorage !== 'undefined') {
            const cached = localStorage.getItem('velocytrack_state_colors');
            if (cached) {
                const parsed = JSON.parse(cached);
                if (parsed && typeof parsed === 'object') {
                    return { ...DEFAULT_STATE_COLORS, ...parsed };
                }
            }
        }
    } catch (e) {
        // ignore
    }
    return { ...DEFAULT_STATE_COLORS };
}

export function setStoredStateColors(colors) {
    if (!colors || typeof colors !== 'object' || Object.keys(colors).length === 0) return;
    try {
        if (typeof localStorage !== 'undefined') {
            const merged = { ...getStoredStateColors(), ...colors };
            localStorage.setItem('velocytrack_state_colors', JSON.stringify(merged));
        }
    } catch (e) {
        // ignore
    }
}

export const DEFAULT_GRID_COLUMNS = [
    { referenceName: 'System.WorkItem', label: 'Work Item', isSystem: true, order: 1, isVisible: true, hidden: false },
    { referenceName: 'System.Comments', label: 'Comments', isSystem: true, order: 2, isVisible: true, hidden: false },
    { referenceName: 'System.AssignedTo', label: 'Assigned', isSystem: true, order: 3, isVisible: true, hidden: false },
    { referenceName: 'System.Title', label: 'Title', isSystem: true, order: 4, isVisible: true, hidden: false },
    {
        referenceName: 'System.State',
        label: 'State',
        isSystem: true,
        order: 5,
        isVisible: true,
        hidden: false,
        allowedValues: [...DEFAULT_STATE_VALUES],
        valueColors: { ...DEFAULT_STATE_COLORS }
    },
    { referenceName: 'System.Capacity', label: 'Capacity', isSystem: true, order: 6, isVisible: true, hidden: false },
    { referenceName: 'System.OriginalEstimate', label: 'Original Estimate', isSystem: true, order: 7, isVisible: true, hidden: false },
    { referenceName: 'System.Progress', label: 'Overall Progress', isSystem: true, order: 8, isVisible: true, hidden: false },
    { referenceName: 'System.Action', label: 'Action', isSystem: true, order: 9, isVisible: true, hidden: false }
];

export const DEFAULT_TIME_GRID_COLUMNS = [
    { referenceName: 'System.WorkItem', label: 'Item', isSystem: true, order: 1, isVisible: true, hidden: false },
    { referenceName: 'System.Title', label: 'Title', isSystem: true, order: 2, isVisible: true, hidden: false },
    {
        referenceName: 'System.State',
        label: 'State',
        isSystem: true,
        order: 3,
        isVisible: true,
        hidden: false,
        allowedValues: [...DEFAULT_STATE_VALUES],
        valueColors: { ...DEFAULT_STATE_COLORS }
    },
    { referenceName: 'System.Progress', label: 'Progress', isSystem: true, order: 4, isVisible: true, hidden: false },
    { referenceName: 'System.WorkType', label: 'Work Type', isSystem: true, order: 5, isVisible: true, hidden: false },
    { referenceName: 'System.Hours', label: 'Completed Work', isSystem: true, order: 6, isVisible: true, hidden: false },
    { referenceName: 'System.Capacity', label: 'Capacity', isSystem: true, order: 7, isVisible: true, hidden: false },

    { referenceName: 'System.Action', label: 'Actions', isSystem: true, order: 8, isVisible: true, hidden: false }
];

export default {
    name: 'ModalTaskColumns',
    mixins: [mixinToast],
    components: {
        'vue-multiselect': Multiselect
    },
    props: {
        currentColumns: {
            type: Array,
            default: () => []
        },
        modalId: {
            type: String,
            default: 'modal-task-columns'
        },
        modalTitle: {
            type: String,
            default: 'Customize Grid Columns'
        },
        subtitle: {
            type: String,
            default: 'Drag and drop column badges below to customize their display sequence in the grid. Use the eye icon to show or hide columns without removing them. Core system columns cannot be removed, and the Action column always remains fixed at the end.'
        },
        saveEndpoint: {
            type: String,
            default: '/TaskManagement/SaveTaskGridColumns'
        },
        defaultColumns: {
            type: Array,
            default: () => DEFAULT_GRID_COLUMNS
        },
        processStates: {
            type: Array,
            default: () => []
        },
        allowCustomFields: {
            type: Boolean,
            default: true
        },
        storageKey: {
            type: String,
            default: ''
        },
        fixedActionRef: {
            type: String,
            default: 'System.Action'
        },
        skipRemoteSave: {
            type: Boolean,
            default: false
        },
        featureConfig: {
            type: Object,
            default: null
        },
        processInfo: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            loadingDiscovery: false,
            discoveryError: null,
            isPermissionError: false,
            discoveredFields: [],
            selectedFieldToAdd: null,
            highlightFieldName: '',
            reorderableColumns: [],
            actionColumn: { referenceName: 'System.Action', label: 'Action', isSystem: true, order: 8 },
            activeColumns: [],
            draggedIndex: null,
            dragOverIndex: null,
            dragOverPos: null,
            saving: false,
            editingColorCol: null,
            colorModalValues: [],
            colorModalMapping: {},
            newCustomValueInput: '',
            presetColors: STATUS_PRESET_COLORS,
            fetchedProcessStates: [],
            fetchedProcessInfo: null,
            selectedWitFilter: 'all',
            draftFeatureEnabled: true,
            draftFeatureItems: [],
            activeSetupTab: 'columns'
        };
    },
    computed: {
        availableFieldsToAdd() {
            const activeRefs = new Set(this.activeColumns.map(c => c.referenceName.toLowerCase()));
            return this.discoveredFields.filter(f => !activeRefs.has(f.referenceName.toLowerCase()));
        },
        colorModalTitle() {
            return this.editingColorCol ? `Assign Colors: ${this.editingColorCol.label}` : 'Assign Value Colors';
        },
        isStateColumn() {
            return !!(this.editingColorCol && this.editingColorCol.referenceName === 'System.State');
        },
        effectiveProcessInfo() {
            return this.processInfo || this.fetchedProcessInfo || null;
        },
        effectiveStatesByWit() {
            const pi = this.effectiveProcessInfo;
            if (pi && pi.statesByWorkItemType && typeof pi.statesByWorkItemType === 'object') {
                return pi.statesByWorkItemType;
            }
            return null;
        },
        availableWitFilters() {
            if (!this.effectiveStatesByWit) return [];
            const list = [];
            const witPriority = {
                'epic': 1, 'feature': 2, 'user story': 3, 'userstory': 3, 'story': 3,
                'product backlog item': 4, 'pbi': 4, 'requirement': 4,
                'issue': 5, 'bug': 6, 'defect': 6, 'task': 7, 'impediment': 8
            };
            const entries = Object.entries(this.effectiveStatesByWit);
            entries.sort(([a], [b]) => {
                const pa = witPriority[a.toLowerCase()] || 99;
                const pb = witPriority[b.toLowerCase()] || 99;
                if (pa !== pb) return pa - pb;
                return a.localeCompare(b);
            });
            for (const [wit, states] of entries) {
                if (!Array.isArray(states) || states.length === 0) continue;
                const validStates = states.map(s => String(s || '').trim()).filter(Boolean);
                if (validStates.length === 0) continue;
                list.push({
                    name: wit,
                    count: validStates.length
                });
            }
            return list;
        },
        dedupedColorModalValues() {
            if (!this.editingColorCol) return [];

            const stateRank = {
                'new': 1, 'to do': 2, 'todo': 2, 'proposed': 3, 'design': 4,
                'active': 10, 'in progress': 11, 'inprogress': 11, 'doing': 12, 'committed': 13, 'started': 14,
                'resolved': 20, 'completed': 21,
                'done': 30, 'closed': 31,
                'removed': 40, 'cut': 41, 'abandoned': 42
            };

            const seen = new Set();
            const result = [];

            const addState = (s) => {
                const val = String(s || '').trim();
                if (!val) return;
                const lower = val.toLowerCase();
                if (seen.has(lower)) return;
                seen.add(lower);

                let wits = [];
                if (this.isStateColumn && this.effectiveStatesByWit) {
                    for (const [wit, states] of Object.entries(this.effectiveStatesByWit)) {
                        if (Array.isArray(states) && states.some(st => String(st || '').trim().toLowerCase() === lower)) {
                            wits.push(wit);
                        }
                    }
                }

                result.push({
                    value: val,
                    wits: wits,
                    rank: stateRank[lower] || 25
                });
            };

            if (this.isStateColumn) {
                if (this.effectiveStatesByWit) {
                    Object.values(this.effectiveStatesByWit).forEach(list => {
                        if (Array.isArray(list)) list.forEach(addState);
                    });
                }
                (this.colorModalValues || []).forEach(addState);
                DEFAULT_STATE_VALUES.forEach(addState);

                result.sort((a, b) => a.rank - b.rank || a.value.localeCompare(b.value));
                return result;
            }

            (this.colorModalValues || []).forEach(v => {
                const val = String(v || '').trim();
                if (val && !seen.has(val.toLowerCase())) {
                    seen.add(val.toLowerCase());
                    result.push({ value: val, wits: [], rank: 0 });
                }
            });
            return result;
        },
        visibleDedupedValues() {
            if (!this.dedupedColorModalValues || !this.dedupedColorModalValues.length) return [];
            if (!this.selectedWitFilter || this.selectedWitFilter === 'all') {
                return this.dedupedColorModalValues;
            }
            const filter = this.selectedWitFilter.toLowerCase();
            return this.dedupedColorModalValues.filter(item =>
                Array.isArray(item.wits) && item.wits.some(w => w.toLowerCase() === filter)
            );
        },
        hasFeatureConfig() {
            return !!(this.featureConfig && this.draftFeatureItems && this.draftFeatureItems.length > 0);
        },
        enabledFeatureCount() {
            if (!this.draftFeatureEnabled) return 0;
            return (this.draftFeatureItems || []).filter(i => i && i.enabled).length;
        },
        uniqueColorModalValues() {
            return this.dedupedColorModalValues.map(item => item.value);
        }
    },
    methods: {
        getStoredSetupTab() {
            if (!this.hasFeatureConfig) return 'columns';
            try {
                if (typeof localStorage !== 'undefined') {
                    const saved = localStorage.getItem('vt_grid_setup_active_tab');
                    if (saved === 'features' || saved === 'columns') {
                        return saved;
                    }
                }
            } catch (e) {
                // ignore storage errors
            }
            return 'columns';
        },
        setSetupTab(tab) {
            const resolved = (tab === 'features' && this.hasFeatureConfig) ? 'features' : 'columns';
            this.activeSetupTab = resolved;
            try {
                if (typeof localStorage !== 'undefined') {
                    localStorage.setItem('vt_grid_setup_active_tab', resolved);
                }
            } catch (e) {
                // ignore storage errors
            }
        },
        showModal(highlightFieldName = '', initialTab = '') {
            const cleanHighlight = typeof highlightFieldName === 'string' ? highlightFieldName.trim() : '';
            if (cleanHighlight) {
                this.activeSetupTab = 'columns';
    
            } else if (initialTab === 'features' || initialTab === 'columns') {
                this.setSetupTab(initialTab);
            } else {
                this.activeSetupTab = this.getStoredSetupTab();
            }
            this.highlightFieldName = typeof highlightFieldName === 'string' ? highlightFieldName.trim() : '';
            this.discoveryError = null;
            if (this.$refs.modalRef) {
                this.$refs.modalRef.show();
            } else {
                this.$bvModal.show(this.modalId);
            }
        },
        checkHighlightField() {
            if (typeof this.highlightFieldName === 'string' && this.highlightFieldName.trim() && this.availableFieldsToAdd && this.availableFieldsToAdd.length) {
                const target = this.highlightFieldName.trim().toLowerCase();
                const match = this.availableFieldsToAdd.find(f =>
                    (f.referenceName && f.referenceName.toLowerCase() === target) ||
                    (f.name && f.name.toLowerCase() === target) ||
                    (f.referenceName && f.referenceName.toLowerCase().endsWith('.' + target))
                );
                if (match) {
                    this.selectedFieldToAdd = match;
                }
            }
        },
        hideModal() {
            this.highlightFieldName = '';
            if (this.$refs.modalRef) {
                this.$refs.modalRef.hide();
            } else {
                this.$bvModal.hide(this.modalId);
            }
        },
        onModalHidden() {

            this.highlightFieldName = '';
            this.selectedFieldToAdd = null;
            this.discoveryError = null;
            this.discoveredFields = [];
        },
        goToConfig() {
            this.hideModal();
            this.$router.push({ name: 'config' });
        },
        async onModalShow() {
            if (!this.highlightFieldName) {
                this.activeSetupTab = this.getStoredSetupTab();
            }
            this.initializeColumns();
            this.initializeFeatureConfig();

            if (this.allowCustomFields) {
                await Promise.all([
                this.discoverFields(),
                this.ensureProcessStates()
            ]);

                this.checkHighlightField();
            }
        },
        async ensureProcessStates() {
            if (this.effectiveStatesByWit && Object.keys(this.effectiveStatesByWit).length > 0) {
                return;
            }
            try {
                const resp = await this.$http.get('/TaskManagement/GetProcessInfo');
                const pi = resp.data;
                if (pi) {
                    this.fetchedProcessInfo = pi;
                    if (pi.statesByWorkItemType) {
                        const states = new Set();
                        Object.values(pi.statesByWorkItemType).forEach(list => {
                            if (Array.isArray(list)) list.forEach(s => states.add(s));
                        });
                        this.fetchedProcessStates = Array.from(states);
                    }
                }
            } catch (e) {
                // Non-fatal fallback
            }
        },
        initializeFeatureConfig() {
            if (!this.featureConfig || typeof this.featureConfig !== 'object') {
                this.draftFeatureEnabled = true;
                this.draftFeatureItems = [];
                return;
            }
            this.draftFeatureEnabled = this.featureConfig.enabled !== false;
            const items = Array.isArray(this.featureConfig.items) ? this.featureConfig.items : [];
            this.draftFeatureItems = items.map(item => ({
                ...item,
                enabled: item.enabled !== false
            }));
        },
        onFeatureCardClick(item, event) {
            if (!this.draftFeatureEnabled || !item) return;
            if (event && event.target && event.target.closest && event.target.closest('.custom-control, input, label')) {
                return;
            }
            this.$set(item, 'enabled', !item.enabled);
        },
        initializeColumns() {
            const baseDefault = this.defaultColumns && this.defaultColumns.length ? this.defaultColumns : DEFAULT_GRID_COLUMNS;
            let cols = [];
            if (this.currentColumns && this.currentColumns.length > 0) {
                cols = this.currentColumns.map((c, idx) => ({
                    key: c.key || c.referenceName,
                    headerClass: c.headerClass || '',
                    referenceName: c.referenceName,
                    label: c.label || c.referenceName,
                    isSystem: c.referenceName.startsWith('System.') || !!c.isSystem,
                    allowedValues: c.allowedValues && c.allowedValues.length ? [...c.allowedValues] : (c.referenceName === 'System.State' ? [...DEFAULT_STATE_VALUES] : []),
                    valueColors: c.referenceName === 'System.State'
                        ? { ...getStoredStateColors(), ...(c.valueColors && typeof c.valueColors === 'object' ? c.valueColors : {}) }
                        : (c.valueColors && typeof c.valueColors === 'object' ? { ...c.valueColors } : {}),
                    workItemTypes: c.workItemTypes && Array.isArray(c.workItemTypes) ? [...c.workItemTypes] : [],
                    fieldType: c.fieldType || '',
                    metricSuffix: c.metricSuffix || '',
                    width: c.width || '',
                    order: c.order || idx + 1,
                    isVisible: c.isVisible !== undefined ? !!c.isVisible : !c.hidden,
                    hidden: c.hidden !== undefined ? !!c.hidden : c.isVisible === false
                }));
            } else {
                cols = baseDefault.map(c => ({
                    ...c,
                    allowedValues: c.allowedValues ? [...c.allowedValues] : [],
                    valueColors: c.valueColors ? { ...c.valueColors } : {},
                    workItemTypes: c.workItemTypes && Array.isArray(c.workItemTypes) ? [...c.workItemTypes] : [],
                    fieldType: c.fieldType || '',
                    metricSuffix: c.metricSuffix || '',
                    width: c.width || '',
                    isVisible: c.isVisible !== undefined ? !!c.isVisible : !c.hidden,
                    hidden: !!c.hidden
                }));
            }

            // Ensure all system columns exist
            baseDefault.forEach(sysCol => {
                const existing = cols.find(c => c.referenceName.toLowerCase() === sysCol.referenceName.toLowerCase());
                if (existing) {
                    existing.isSystem = true;
                    if (sysCol.key && !existing.key) existing.key = sysCol.key;
                    if (sysCol.headerClass !== undefined && !existing.headerClass) existing.headerClass = sysCol.headerClass;
                    if (!existing.label) existing.label = sysCol.label;
                    if ((!existing.allowedValues || !existing.allowedValues.length) && sysCol.allowedValues) {
                        existing.allowedValues = [...sysCol.allowedValues];
                    }
                    if ((!existing.valueColors || Object.keys(existing.valueColors).length === 0) && sysCol.valueColors) {
                        existing.valueColors = { ...sysCol.valueColors };
                    } else if (existing.referenceName === 'System.State') {
                        existing.valueColors = { ...getStoredStateColors(), ...(existing.valueColors || {}) };
                    }
                } else {
                    cols.push({
                        ...sysCol,
                        allowedValues: sysCol.allowedValues ? [...sysCol.allowedValues] : [],
                        valueColors: sysCol.valueColors ? { ...sysCol.valueColors } : {},
                        workItemTypes: [],
                        order: cols.length + 1,
                        isVisible: sysCol.isVisible !== undefined ? !!sysCol.isVisible : !sysCol.hidden,
                        hidden: sysCol.hidden !== undefined ? !!sysCol.hidden : sysCol.isVisible === false


                    });
                }
            });

            cols.sort((a, b) => (a.order || 0) - (b.order || 0));

            // System.Action must always be last and visible
            const fixedRef = this.fixedActionRef || 'System.Action';
            const actionCol = cols.find(c => c.referenceName === fixedRef) || { referenceName: fixedRef, key: fixedRef, label: 'Action', isSystem: true };
            this.actionColumn = { ...actionCol, isSystem: true, isVisible: true, hidden: false };
            this.reorderableColumns = cols.filter(c => c.referenceName !== fixedRef);
            this.syncActiveColumns();
        },
        syncActiveColumns() {
            const fixedRef = this.fixedActionRef || 'System.Action';
            const action = this.actionColumn || { referenceName: fixedRef, key: fixedRef, label: 'Action', isSystem: true, isVisible: true, hidden: false };
            const combined = [...this.reorderableColumns, action];
            this.activeColumns = combined.map((c, i) => ({
                ...c,
                allowedValues: c.allowedValues || [],
                valueColors: c.valueColors && typeof c.valueColors === 'object' ? { ...c.valueColors } : {},
                workItemTypes: c.workItemTypes || [],
                order: i + 1,
                isVisible: c.isVisible !== undefined ? !!c.isVisible : !c.hidden,
                hidden: c.hidden !== undefined ? !!c.hidden : c.isVisible === false
            }));
        },
        toggleColumnVisibility(col) {
            if (!col) return;
            const newHidden = !col.hidden;
            this.$set(col, 'hidden', newHidden);
            this.$set(col, 'isVisible', !newHidden);
            this.syncActiveColumns();
        },
        async discoverFields(forceRefresh = false) {
            this.loadingDiscovery = true;
            this.discoveryError = null;
            this.isPermissionError = false;
            try {
                const endpoint = forceRefresh ? '/TaskManagement/DiscoverCustomFields?refresh=true' : '/TaskManagement/DiscoverCustomFields';
                const response = await this.$http.get(endpoint);
                this.discoveredFields = response.data || [];
                // Enrich active and reorderable columns with discovered allowedValues, workItemTypes, metricSuffix if empty
                this.reorderableColumns.forEach(c => {
                    const df = this.discoveredFields.find(f => f.referenceName.toLowerCase() === c.referenceName.toLowerCase());
                    if (df) {
                        if (!c.allowedValues || !c.allowedValues.length) {
                            if (df.allowedValues && df.allowedValues.length) {
                                c.allowedValues = [...df.allowedValues];
                            } else if ((df.fieldType || '').toLowerCase() === 'boolean') {
                                c.allowedValues = ['Yes', 'No'];
                            }
                        }
                        if ((!c.workItemTypes || !c.workItemTypes.length) && df.workItemTypes && df.workItemTypes.length) {
                            c.workItemTypes = [...df.workItemTypes];
                        }
                        if (!c.fieldType && df.fieldType) {
                            c.fieldType = df.fieldType;
                        }
                        if (!c.metricSuffix && df.metricSuffix) {
                            c.metricSuffix = df.metricSuffix;
                        }
                    }
                });
                this.syncActiveColumns();
                this.checkHighlightField();
            } catch (err) {
                const status = err.response?.status;
                const data = err.response?.data;
                const isPerm = status === 403 || status === 401 ||
                    data?.isPermissionDenied ||
                    data?.error === 'PermissionDenied' ||
                    (typeof data?.message === 'string' && (
                        data.message.includes('Access Denied') ||
                        data.message.includes('Unauthorized') ||
                        data.message.includes('TF400813') ||
                        data.message.includes('Permission')
                    ));

                this.isPermissionError = !!isPerm;
                this.discoveryError = data?.message ||
                    (typeof data === 'string' ? data : err.message) ||
                    'Failed to discover custom fields.';
            } finally {
                this.loadingDiscovery = false;
            }
        },
        onFieldSelected(field) {
            this.selectedFieldToAdd = field;
        },
        addSelectedColumn() {
            if (!this.selectedFieldToAdd) return;
            const field = this.selectedFieldToAdd;
            const exists = this.activeColumns.some(c => c.referenceName.toLowerCase() === field.referenceName.toLowerCase());
            if (exists) {
                this.toastWarning('Already Added', `Column "${field.name}" is already in your active list.`);
                this.selectedFieldToAdd = null;
                return;
            }

            let allowedValues = field.allowedValues || [];
            if (!allowedValues.length && (field.fieldType || '').toLowerCase() === 'boolean') {
                allowedValues = ['Yes', 'No'];
            }

            this.reorderableColumns.push({
                referenceName: field.referenceName,
                label: field.name,
                isSystem: false,
                allowedValues: allowedValues,
                valueColors: field.valueColors && typeof field.valueColors === 'object' ? { ...field.valueColors } : {},
                workItemTypes: field.workItemTypes && Array.isArray(field.workItemTypes) ? [...field.workItemTypes] : [],
                fieldType: field.fieldType || '',
                metricSuffix: field.metricSuffix || '',
                width: '',
                order: this.reorderableColumns.length + 1,
                isVisible: true,
                hidden: false




            });
            this.syncActiveColumns();

            this.toastSuccess('Column Added', `"${field.name}" added to active grid columns.`);
            this.selectedFieldToAdd = null;
        },
        removeColumn(index) {
            const col = this.reorderableColumns[index];
            if (!col || col.isSystem) return;
            this.reorderableColumns.splice(index, 1);
            this.syncActiveColumns();
            this.toastInfo('Column Removed', `"${col.label}" removed from grid.`);
        },
        onDragStart(idx, event) {
            this.draggedIndex = idx;
            if (event && event.dataTransfer) {
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', String(idx));
            }
        },
        onDragOver(idx, event) {
            if (this.draggedIndex === null || this.draggedIndex === undefined || this.draggedIndex === idx) {
                this.dragOverIndex = null;
                this.dragOverPos = null;
                return;
            }
            const rect = event.currentTarget.getBoundingClientRect();
            const midX = rect.left + rect.width / 2;
            this.dragOverIndex = idx;
            this.dragOverPos = event.clientX < midX ? 'left' : 'right';
            if (event && event.dataTransfer) {
                event.dataTransfer.dropEffect = 'move';
            }
        },
        onDragLeave(idx) {
            if (this.dragOverIndex === idx) {
                this.dragOverIndex = null;
                this.dragOverPos = null;
            }
        },
        onDrop(targetIdx) {
            if (this.draggedIndex === null || this.draggedIndex === undefined) return;
            const fromIdx = this.draggedIndex;
            let toIdx = targetIdx;

            if (this.dragOverPos === 'right') {
                toIdx = fromIdx < targetIdx ? targetIdx : targetIdx + 1;
            } else {
                toIdx = fromIdx > targetIdx ? targetIdx : targetIdx - 1;
            }

            if (toIdx < 0) toIdx = 0;
            if (toIdx >= this.reorderableColumns.length) toIdx = this.reorderableColumns.length - 1;

            if (fromIdx !== toIdx) {
                const item = this.reorderableColumns.splice(fromIdx, 1)[0];
                this.reorderableColumns.splice(toIdx, 0, item);
                this.syncActiveColumns();
            }
            this.onDragEnd();
        },
        onDragEnd() {
            this.draggedIndex = null;
            this.dragOverIndex = null;
            this.dragOverPos = null;
        },
        resetToDefaultColumns() {
            const baseDefault = this.defaultColumns && this.defaultColumns.length ? this.defaultColumns : DEFAULT_GRID_COLUMNS;
            const defaultCols = baseDefault.map(c => ({
                ...c,
                allowedValues: c.allowedValues ? [...c.allowedValues] : [],
                valueColors: c.valueColors ? { ...c.valueColors } : {},
                width: c.width || '',
                isVisible: c.isVisible !== undefined ? !!c.isVisible : !c.hidden,
                hidden: c.hidden !== undefined ? !!c.hidden : c.isVisible === false


            }));
            const fixedRef = this.fixedActionRef || 'System.Action';
            const actionCol = defaultCols.find(c => c.referenceName === fixedRef) || { referenceName: fixedRef, key: fixedRef, label: 'Action', isSystem: true };
            this.actionColumn = { ...actionCol, isSystem: true, isVisible: true, hidden: false };
            this.reorderableColumns = defaultCols.filter(c => c.referenceName !== fixedRef);
            this.syncActiveColumns();
            if (this.featureConfig && this.draftFeatureItems.length > 0) {
                this.draftFeatureEnabled = true;
                this.draftFeatureItems.forEach(item => {
                    this.$set(item, 'enabled', true);
                });
            }
            this.toastInfo('Defaults Restored', 'Columns and grid setup reset to defaults. Click Save to apply.');
        },
        async saveColumns() {
            this.saving = true;
            try {
                this.syncActiveColumns();
                const payload = this.activeColumns.map((c, index) => ({
                    key: c.key || c.referenceName,
                    headerClass: c.headerClass || '',
                    referenceName: c.referenceName,
                    label: c.label,
                    isSystem: !!c.isSystem,
                    allowedValues: c.allowedValues || [],
                    valueColors: c.valueColors && typeof c.valueColors === 'object' ? c.valueColors : {},
                    workItemTypes: c.workItemTypes && Array.isArray(c.workItemTypes) ? [...c.workItemTypes] : [],
                    fieldType: c.fieldType || '',
                    metricSuffix: c.metricSuffix || '',
                    width: c.width || '',
                    order: index + 1,
                    isVisible: c.isVisible !== undefined ? !!c.isVisible : !c.hidden,
                    hidden: c.hidden !== undefined ? !!c.hidden : c.isVisible === false
                }));

                const stateCol = payload.find(c => c.referenceName === 'System.State');
                if (stateCol && stateCol.valueColors && Object.keys(stateCol.valueColors).length > 0) {
                    try {
                        setStoredStateColors(stateCol.valueColors);
                    } catch (e) {
                        // ignore
                    }
                }

                if (this.storageKey) {
                    try {
                        if (typeof localStorage !== 'undefined') {
                            localStorage.setItem(this.storageKey, JSON.stringify(payload));
                        }
                    } catch (e) {
                        // ignore storage quota errors
                    }
                } else {
                    if (this.saveEndpoint && !this.skipRemoteSave) {
                        await this.$http.post(this.saveEndpoint, payload);
                    }
                }
                if (this.featureConfig && this.draftFeatureItems.length > 0) {
                    const featureState = {
                        enabled: !!this.draftFeatureEnabled
                    };
                    this.draftFeatureItems.forEach(item => {
                        if (item && item.key) {
                            featureState[item.key] = !!item.enabled;
                        }
                    });
                    if (this.featureConfig.storageKey && typeof localStorage !== 'undefined') {
                        try {
                            localStorage.setItem(this.featureConfig.storageKey, JSON.stringify(featureState));
                        } catch (e) {
                            // ignore storage quota errors
                        }
                    }
                    this.$emit('feature-settings-saved', featureState);
                }
                this.$emit('columns-saved', payload);
                this.toastSuccess('Columns Saved', 'Grid columns updated successfully.');
                this.hideModal();
            } catch (err) {
                const msg = err.response?.data?.message || err.response?.data || err.message || 'Failed to save column configuration.';
                this.toastError('Save Failed', typeof msg === 'string' ? msg : JSON.stringify(msg));
            } finally {
                this.saving = false;
            }
        },
        formatWorkItemTypesSummary(types) {
            if (!types || !Array.isArray(types) || types.length === 0) return '';
            if (types.length <= 2) {
                return types.join(', ');
            }
            return `${types[0]}, ${types[1]} +${types.length - 2}`;
        },
        hasEnumeratedValues(col) {
            if (!col) return false;
            if (col.referenceName === 'System.State') return true;
            if (Array.isArray(col.allowedValues) && col.allowedValues.length > 0) return true;
            const df = (this.discoveredFields || []).find(f => f.referenceName.toLowerCase() === col.referenceName.toLowerCase());
            if (df && ((df.allowedValues && df.allowedValues.length > 0) || (df.fieldType || '').toLowerCase() === 'picklist' || (df.fieldType || '').toLowerCase() === 'boolean')) {
                return true;
            }
            return false;
        },
        hasCustomColors(col) {
            return !!(col && col.valueColors && typeof col.valueColors === 'object' && Object.keys(col.valueColors).length > 0);
        },
        openValueColors(col) {
            if (!col) return;
            this.editingColorCol = col;
            this.selectedWitFilter = 'all';
            let values = [];
            if (col.referenceName === 'System.State') {
                const allKnown = [
                    ...(this.processStates || []),
                    ...(this.fetchedProcessStates || []),
                    ...(col.allowedValues || []),
                    ...(col.valueColors ? Object.keys(col.valueColors) : []),
                    ...DEFAULT_STATE_VALUES
                ];
                if (this.effectiveStatesByWit) {
                    Object.values(this.effectiveStatesByWit).forEach(list => {
                        if (Array.isArray(list)) allKnown.push(...list);
                    });
                }
                const seen = new Set();
                values = [];
                for (const item of allKnown) {
                    const norm = String(item || '').trim();
                    if (norm && !seen.has(norm.toLowerCase())) {
                        seen.add(norm.toLowerCase());
                        values.push(norm);
                    }
                }
            } else if (Array.isArray(col.allowedValues) && col.allowedValues.length > 0) {
                values = [...col.allowedValues];
            } else {
                const df = (this.discoveredFields || []).find(f => f.referenceName.toLowerCase() === col.referenceName.toLowerCase());
                if (df && df.allowedValues && df.allowedValues.length > 0) {
                    values = [...df.allowedValues];
                } else if (df && (df.fieldType || '').toLowerCase() === 'boolean') {
                    values = ['Yes', 'No'];
                }
            }
            if (col.valueColors && typeof col.valueColors === 'object') {
                Object.keys(col.valueColors).forEach(k => {
                    if (!values.some(v => v.toLowerCase() === k.toLowerCase())) {
                        values.push(k);
                    }
                });
            }
            this.colorModalValues = values;

            const mapping = col.valueColors && typeof col.valueColors === 'object' ? { ...col.valueColors } : {};
            if (col.referenceName === 'System.State') {
                const allStateNames = new Set(values);
                if (this.effectiveStatesByWit) {
                    Object.values(this.effectiveStatesByWit).forEach(list => {
                        if (Array.isArray(list)) list.forEach(s => allStateNames.add(s));
                    });
                }
                allStateNames.forEach(v => {
                    if (!mapping[v]) {
                        const match = Object.entries(getStoredStateColors()).find(([k]) => k.toLowerCase() === v.toLowerCase());
                        if (match) {
                            mapping[v] = match[1];
                        }
                    }
                });
            }
            this.colorModalMapping = mapping;
            this.newCustomValueInput = '';

            if (col.referenceName === 'System.State' && (!this.effectiveStatesByWit || Object.keys(this.effectiveStatesByWit).length === 0)) {
                this.ensureProcessStates();
            }

            if (this.$refs.modalValueColorsRef) {
                this.$refs.modalValueColorsRef.show();
            } else {
                this.$bvModal.show('modal-col-value-colors');
            }
        },
        closeValueColorsModal() {
            if (this.$refs.modalValueColorsRef) {
                this.$refs.modalValueColorsRef.hide();
            } else {
                this.$bvModal.hide('modal-col-value-colors');
            }
        },
        onColorModalHidden() {
            this.editingColorCol = null;
            this.colorModalValues = [];
            this.colorModalMapping = {};
            this.newCustomValueInput = '';
            this.selectedWitFilter = 'all';
        },
        applyValueColor(val, hex) {
            if (!val) return;
            this.$set(this.colorModalMapping, val, hex);
        },
        clearValueColor(val) {
            if (!val) return;
            this.$delete(this.colorModalMapping, val);
        },
        clearAllValueColors() {
            this.colorModalMapping = {};
        },
        autoAssignPalette() {
            const allValues = this.uniqueColorModalValues;
            if (!allValues || !allValues.length) return;
            const mapping = {};
            allValues.forEach((val, i) => {
                const swatch = this.presetColors[i % this.presetColors.length];
                mapping[val] = swatch.hex;
            });
            this.colorModalMapping = mapping;
        },
        addNewCustomValue() {
            const val = (this.newCustomValueInput || '').trim();
            if (!val) return;
            if (!this.colorModalValues.some(v => v.toLowerCase() === val.toLowerCase())) {
                this.colorModalValues.push(val);
            }
            if (!this.colorModalMapping[val]) {
                const match = Object.entries(DEFAULT_STATE_COLORS).find(([k]) => k.toLowerCase() === val.toLowerCase());
                if (match) {
                    this.$set(this.colorModalMapping, val, match[1]);
                }
            }
            this.newCustomValueInput = '';
        },
        getValuePreviewStyle(val) {
            const hex = this.colorModalMapping[val];
            if (!hex) {
                return {
                    backgroundColor: '#e2e8f0',
                    color: '#0f172a',
                    border: '1px solid #cbd5e1',
                    fontWeight: '600'
                };
            }
            const textColor = this.getContrastingTextColor(hex);
            return {
                backgroundColor: hex,
                color: textColor,
                border: `1px solid ${hex}`,
                fontWeight: '600'
            };
        },
        getContrastingTextColor(hex) {
            if (!hex || typeof hex !== 'string') return '#ffffff';
            let c = hex.replace('#', '');
            if (c.length === 3) {
                c = c.split('').map(x => x + x).join('');
            }
            if (c.length !== 6) return '#ffffff';
            const r = parseInt(c.substring(0, 2), 16);
            const g = parseInt(c.substring(2, 4), 16);
            const b = parseInt(c.substring(4, 6), 16);
            const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
            return yiq >= 150 ? '#0f172a' : '#ffffff';
        },
        isColorSelected(val, hex) {
            const current = this.colorModalMapping[val];
            if (!current) return false;
            return current.toLowerCase() === hex.toLowerCase();
        },
        saveValueColors() {
            if (!this.editingColorCol) return;
            this.$set(this.editingColorCol, 'valueColors', { ...this.colorModalMapping });
            if (this.editingColorCol.referenceName === 'System.State') {
                setStoredStateColors(this.colorModalMapping);
            }
            const allValues = this.uniqueColorModalValues;
            if (allValues && allValues.length) {
                this.$set(this.editingColorCol, 'allowedValues', [...allValues]);
            }
            this.syncActiveColumns();
            this.closeValueColorsModal();
            this.toastSuccess('Colors Assigned', `Colors updated for "${this.editingColorCol.label}". Remember to click Save Columns.`);
        },
        getWitIcon(witName) {
            if (!witName) return '';
            return resolveWorkItemTypeIcon('', witName);
        }
    }
};
</script>

<style scoped>
.live-sequence-container {
    user-select: none;
    padding: 0.5rem 0.25rem;
}

.flow-pill {
    font-size: 0.82rem;
    font-weight: 500;
    transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.draggable-pill {
    cursor: grab;
    position: relative;
}

.draggable-pill:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

.draggable-pill:active {
    cursor: grabbing;
}

.draggable-pill.is-dragging {
    opacity: 0.35;
    transform: scale(0.96);
    border: 1px dashed #6c757d;
}

.draggable-pill.is-drop-target-left {
    box-shadow: -4px 0 0 0 #0d6efd, 0 4px 8px rgba(0, 0, 0, 0.15) !important;
    border-left-color: #0d6efd !important;
}

.draggable-pill.is-drop-target-right {
    box-shadow: 4px 0 0 0 #0d6efd, 0 4px 8px rgba(0, 0, 0, 0.15) !important;
    border-right-color: #0d6efd !important;
}

.grip-handle {
    opacity: 0.85;
    font-size: 0.85rem;
    cursor: grab;
    color: #38bdf8;
}

.pill-order-num {
    font-size: 0.75rem;
    font-weight: 600;
}

.system-flow-pill {
    background-color: #ffffff !important;
    border: 1px solid #cbd5e1 !important;
    color: #1e293b !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.system-flow-pill:hover {
    background-color: #f8fafc !important;
    border-color: #94a3b8 !important;
}

.system-flow-pill .pill-order-num {
    color: #64748b;
}

.system-flow-pill .grip-handle {
    color: #94a3b8;
}

[data-theme="dark"] .system-flow-pill {
    background-color: #1e293b !important;
    border-color: #334155 !important;
    color: #f1f5f9 !important;
}

[data-theme="dark"] .system-flow-pill:hover {
    background-color: #27354a !important;
    border-color: #475569 !important;
}

[data-theme="dark"] .system-flow-pill .pill-order-num {
    color: #94a3b8;
}

[data-theme="dark"] .system-flow-pill .grip-handle {
    color: #38bdf8;
}

.custom-flow-pill {
    background-color: #eff6ff !important;
    border: 1px solid #93c5fd !important;
    color: #1d4ed8 !important;
    box-shadow: 0 1px 3px rgba(37, 99, 235, 0.08);
}

.custom-flow-pill:hover {
    background-color: #dbeafe !important;
    border-color: #60a5fa !important;
}

.custom-flow-pill .pill-order-num {
    color: #3b82f6;
}

.custom-flow-pill .grip-handle {
    color: #3b82f6;
}

[data-theme="dark"] .custom-flow-pill {
    background-color: rgba(30, 64, 175, 0.35) !important;
    border-color: #3b82f6 !important;
    color: #93c5fd !important;
}

[data-theme="dark"] .custom-flow-pill:hover {
    background-color: rgba(30, 64, 175, 0.55) !important;
    border-color: #60a5fa !important;
}

[data-theme="dark"] .custom-flow-pill .pill-order-num {
    color: #60a5fa;
}

[data-theme="dark"] .custom-flow-pill .grip-handle {
    color: #38bdf8;
}

.locked-action-pill {
    cursor: default;
    border: 1px solid #cbd5e1 !important;
    background-color: #f8fafc !important;
    color: #334155 !important;
}

.locked-action-pill .pill-order-num {
    color: #64748b;
}

.action-fixed-badge {
    background-color: #e2e8f0;
    color: #475569;
}

[data-theme="dark"] .locked-action-pill {
    border-color: #334155 !important;
    background-color: #0f172a !important;
    color: #cbd5e1 !important;
}

[data-theme="dark"] .locked-action-pill .pill-order-num {
    color: #64748b;
}

[data-theme="dark"] .action-fixed-badge {
    background-color: #1e293b;
    color: #94a3b8;
}

.flow-pill-remove {
    cursor: pointer;
    font-size: 0.95rem;
    line-height: 1;
    font-weight: bold;
    color: #ef4444;
    background: rgba(239, 68, 68, 0.12);
    border-radius: 4px;
    padding: 1px 5px;
    transition: transform 0.15s, background-color 0.15s, color 0.15s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.flow-pill-remove:hover {
    transform: scale(1.18);
    color: #ffffff;
    background-color: #ef4444;
}

[data-theme="dark"] .flow-pill-remove {
    color: #f87171;
    background: rgba(0, 0, 0, 0.25);
}

[data-theme="dark"] .flow-pill-remove:hover {
    color: #ffffff;
    background-color: #ef4444;
}

.flow-pill-lock {
    color: #fbbf24;
    opacity: 0.9;
}

.font-monospace {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.gap-2 {
    gap: 0.5rem;
}

.flow-pill-hidden {
    opacity: 0.65;
    border-style: dashed !important;
    background-color: #f1f5f9 !important;
    color: #94a3b8 !important;
}

[data-theme="dark"] .flow-pill-hidden {
    background-color: rgba(30, 41, 59, 0.4) !important;
    border-color: #475569 !important;
    color: #64748b !important;
}

.flow-pill-eye {
    line-height: 1;
    font-size: 0.85rem;
    vertical-align: middle;
    transition: transform 0.15s ease, background-color 0.15s ease;
    border: none;
    background: rgba(0, 0, 0, 0.07);
    border-radius: 4px;
    padding: 3px 5px !important;
    text-decoration: none !important;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.flow-pill-eye:hover {
    transform: scale(1.18);
    background: rgba(0, 0, 0, 0.14);
}

[data-theme="dark"] .flow-pill-eye {
    background: rgba(255, 255, 255, 0.15);
}

[data-theme="dark"] .flow-pill-eye:hover {
    background: rgba(255, 255, 255, 0.28);
}

.flow-pill-eye-visible {
    color: #4ade80 !important;
}

[data-theme="light"] .flow-pill-eye-visible {
    color: #16a34a !important;
}

.flow-pill-eye-hidden {
    color: #f59e0b !important;
}

.pill-hidden-badge {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 0.15rem 0.35rem;
    background-color: #64748b;
    color: #ffffff;
}

[data-theme="dark"] .pill-hidden-badge {
    background-color: #334155;
    color: #cbd5e1;
}

.flow-pill-palette {
    line-height: 1;
    font-size: 0.85rem;
    vertical-align: middle;
    transition: transform 0.15s ease, background-color 0.15s ease;
    border: none;
    background: rgba(0, 0, 0, 0.16);
    border-radius: 4px;
    padding: 3px 5px !important;
    text-decoration: none !important;
    color: #fbbf24 !important;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.flow-pill-palette:hover {
    transform: scale(1.18);
    background: rgba(0, 0, 0, 0.28);
    color: #f59e0b !important;
}

::v-deep .modal-col-value-colors-dialog {
    z-index: 1060 !important;
}

::v-deep .modal-col-value-colors-dialog .modal-dialog {
    max-width: 720px;
    width: 92vw;
}

.wit-tabs-bar {
    max-height: 80px;
    overflow-y: auto;
}

.wit-tab-icon {
    line-height: 1;
}

.wit-tab-icon svg {
    width: 12px;
    height: 12px;
    vertical-align: middle;
}

.wit-chip-icon {
    line-height: 1;
}

.wit-chip-icon svg {
    width: 11px;
    height: 11px;
    vertical-align: middle;
}

.wit-usage-chip {
    font-size: 0.72rem;
    padding: 0.15rem 0.35rem;
    background-color: #ffffff;
    border-color: #cbd5e1 !important;
}

[data-theme="dark"] .wit-usage-chip {
    background-color: #1e293b !important;
    border-color: #334155 !important;
    color: #cbd5e1 !important;
}

.color-values-container {
    max-height: 420px;
    overflow-y: auto;
    padding-right: 4px;
}

.wit-group-header {
    background-color: #e2e8f0;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 0.35rem 0.6rem;
    font-size: 0.82rem;
}

[data-theme="dark"] .wit-group-header {
    background-color: #1e293b;
    border-color: #334155;
}

.wit-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
}

.wit-icon svg {
    width: 14px;
    height: 14px;
    vertical-align: middle;
}

.color-values-list {
    max-height: none;
}

.color-value-row {
    background-color: #f1f5f9;
    border-color: #cbd5e1 !important;
}

[data-theme="dark"] .color-value-row {
    background-color: rgba(30, 41, 59, 0.6);
    border-color: #334155 !important;
}

.value-preview-badge {
    font-size: 0.8rem;
    max-width: 100%;
    white-space: nowrap;
    letter-spacing: 0.2px;
}

.color-swatch-btn {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid transparent;
    padding: 0;
    cursor: pointer;
    transition: transform 0.15s ease, border-color 0.15s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.color-swatch-btn:hover {
    transform: scale(1.25);
    z-index: 2;
}

.color-swatch-btn.is-selected {
    border-color: #ffffff;
    box-shadow: 0 0 0 2px #0d6efd, 0 2px 4px rgba(0, 0, 0, 0.3);
    transform: scale(1.15);
}

.color-picker-label {
    cursor: pointer;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 4px;
    border: 1px dashed #cbd5e1;
    background-color: #f1f5f9;
    transition: background-color 0.15s ease;
}

.color-picker-label:hover {
    background-color: #e2e8f0;
}

.color-picker-native {
    position: absolute;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
}

.color-clear-btn {
    font-size: 1.1rem;
    line-height: 1;
    color: #94a3b8 !important;
    text-decoration: none !important;
}

.color-clear-btn:hover {
    color: #ef4444 !important;
}
.pill-types-badge {
    font-size: 0.72rem;
    padding: 0.12rem 0.3rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #475569;
    background-color: #f8fafc;
    border-color: #cbd5e1 !important;
    cursor: help;
    vertical-align: middle;
    line-height: 1;
}

[data-theme="dark"] .pill-types-badge {
    color: #cbd5e1;
    background-color: #1e293b;
    border-color: #475569 !important;
}
/* Autocomplete results dropdown distinguishing background */
.custom-fields-autocomplete ::v-deep .multiselect__content-wrapper {
    background-color: #f1f5f9 !important;
    border: 1px solid #94a3b8 !important;
    border-radius: 6px !important;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.2), 0 4px 10px rgba(15, 23, 42, 0.1) !important;
    z-index: 1055 !important;
}

.custom-fields-autocomplete ::v-deep .multiselect__content {
    background-color: transparent !important;
}

.custom-fields-autocomplete ::v-deep .multiselect__option {
    background-color: #f1f5f9 !important;
    border-bottom: 1px solid #e2e8f0;
    padding: 10px 14px;
    transition: background-color 0.15s ease;
}

.custom-fields-autocomplete ::v-deep .multiselect__option--highlight {
    background-color: #0d6efd !important;
    color: #ffffff !important;
}

.custom-fields-autocomplete ::v-deep .multiselect__option--highlight .text-dark,
.custom-fields-autocomplete ::v-deep .multiselect__option--highlight .text-muted {
    color: #ffffff !important;
}

.custom-fields-autocomplete ::v-deep .multiselect__option--highlight .badge-light {
    background-color: rgba(255, 255, 255, 0.25) !important;
    color: #ffffff !important;
    border-color: rgba(255, 255, 255, 0.4) !important;
}

/* Dark mode: distinctly deeper surface with vibrant border and elevated shadow */
[data-theme="dark"] .custom-fields-autocomplete ::v-deep .multiselect__content-wrapper {
    background-color: #0c1424 !important;
    border: 1px solid #3b82f6 !important;
    box-shadow: 0 16px 36px rgba(0, 0, 0, 0.85), 0 0 0 1px rgba(59, 130, 246, 0.35) !important;
    border-radius: 6px !important;
    z-index: 1055 !important;
}

[data-theme="dark"] .custom-fields-autocomplete ::v-deep .multiselect__option {
    background-color: #0c1424 !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    color: #e2e8f0 !important;
}

[data-theme="dark"] .custom-fields-autocomplete ::v-deep .multiselect__option .text-dark {
    color: #f8fafc !important;
}

[data-theme="dark"] .custom-fields-autocomplete ::v-deep .multiselect__option .text-muted {
    color: #94a3b8 !important;
}

[data-theme="dark"] .custom-fields-autocomplete ::v-deep .multiselect__option .badge-light {
    background-color: #1e293b !important;
    color: #cbd5e1 !important;
    border-color: #334155 !important;
}

[data-theme="dark"] .custom-fields-autocomplete ::v-deep .multiselect__option--highlight {
    background-color: #2563eb !important;
    color: #ffffff !important;
}

[data-theme="dark"] .custom-fields-autocomplete ::v-deep .multiselect__option--highlight .text-dark,
[data-theme="dark"] .custom-fields-autocomplete ::v-deep .multiselect__option--highlight .text-muted {
    color: #ffffff !important;
}

[data-theme="dark"] .custom-fields-autocomplete ::v-deep .multiselect__option--highlight .badge-light {
    background-color: rgba(255, 255, 255, 0.2) !important;
    color: #ffffff !important;
    border-color: rgba(255, 255, 255, 0.35) !important;
}
/* Setup Tabs Bar */
.grid-setup-tabs {
    background-color: #f1f5f9;
    border-color: #cbd5e1 !important;
}

[data-theme="dark"] .grid-setup-tabs {
    background-color: #0f172a;
    border-color: #334155 !important;
}

.grid-setup-tab-btn {
    background: transparent;
    color: #475569;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
    outline: none !important;
}

.grid-setup-tab-btn:hover:not(.is-active) {
    background-color: rgba(148, 163, 184, 0.15);
    color: #0f172a;
}

.grid-setup-tab-btn.is-active {
    background-color: #ffffff;
    color: #0d6efd;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1), 0 0 0 1px rgba(203, 213, 225, 0.7);
}

[data-theme="dark"] .grid-setup-tab-btn {
    color: #94a3b8;
}

[data-theme="dark"] .grid-setup-tab-btn:hover:not(.is-active) {
    background-color: rgba(51, 65, 85, 0.45);
    color: #f1f5f9;
}

[data-theme="dark"] .grid-setup-tab-btn.is-active {
    background-color: #1e293b;
    color: #60a5fa;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(59, 130, 246, 0.35);
}

.grid-setup-tab-badge {
    background-color: rgba(100, 116, 139, 0.15);
    color: #334155;
    border: 1px solid rgba(100, 116, 139, 0.3);
}

.grid-setup-tab-btn.is-active .grid-setup-tab-badge {
    background-color: rgba(13, 110, 253, 0.12);
    color: #0d6efd;
    border-color: rgba(13, 110, 253, 0.3);
}

[data-theme="dark"] .grid-setup-tab-badge {
    background-color: rgba(100, 116, 139, 0.25);
    color: #cbd5e1;
    border-color: rgba(100, 116, 139, 0.4);
}

[data-theme="dark"] .grid-setup-tab-btn.is-active .grid-setup-tab-badge {
    background-color: rgba(59, 130, 246, 0.2);
    color: #93c5fd;
    border-color: rgba(59, 130, 246, 0.45);
}

@media (max-width: 640px) {
    .grid-setup-tabs {
        flex-direction: column;
        align-items: stretch !important;
        gap: 0.25rem;
    }

    .grid-setup-tab-btn {
        margin-left: 0 !important;
    }
}

/* Grid-Specific Feature & Hygiene Setup Card */
.grid-feature-setup-card {
    background-color: #f8fafc;
    border-color: #cbd5e1 !important;
}

[data-theme="dark"] .grid-feature-setup-card {
    background-color: #0f172a;
    border-color: #334155 !important;
}

.feature-setup-header {
    border-color: #e2e8f0 !important;
}

[data-theme="dark"] .feature-setup-header {
    border-color: #1e293b !important;
}

.feature-setup-title {
    color: #1e293b;
}

[data-theme="dark"] .feature-setup-title {
    color: #f1f5f9;
}

.feature-status-badge--on {
    background-color: rgba(16, 185, 129, 0.15);
    color: #047857;
    border: 1px solid rgba(16, 185, 129, 0.35);
}

[data-theme="dark"] .feature-status-badge--on {
    background-color: rgba(16, 185, 129, 0.2);
    color: #6ee7b7;
    border-color: rgba(16, 185, 129, 0.4);
}

.feature-status-badge--off {
    background-color: rgba(100, 116, 139, 0.15);
    color: #475569;
    border: 1px solid rgba(100, 116, 139, 0.35);
}

[data-theme="dark"] .feature-status-badge--off {
    background-color: rgba(100, 116, 139, 0.25);
    color: #94a3b8;
    border-color: rgba(100, 116, 139, 0.4);
}

.feature-case-card {
    background-color: #ffffff;
    border-color: #e2e8f0 !important;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease, box-shadow 0.15s ease;
}

.feature-case-card:hover:not(.is-master-disabled) {
    border-color: #93c5fd !important;
    box-shadow: 0 2px 6px rgba(37, 99, 235, 0.08);
}

.feature-case-card.is-case-enabled {
    border-color: #bfdbfe !important;
}

.feature-case-card.is-case-disabled {
    opacity: 0.72;
    background-color: #f1f5f9;
}

.feature-case-card.is-master-disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #f1f5f9;
}

[data-theme="dark"] .feature-case-card {
    background-color: #1e293b;
    border-color: #334155 !important;
}

[data-theme="dark"] .feature-case-card:hover:not(.is-master-disabled) {
    border-color: #3b82f6 !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}

[data-theme="dark"] .feature-case-card.is-case-enabled {
    border-color: rgba(59, 130, 246, 0.5) !important;
}

[data-theme="dark"] .feature-case-card.is-case-disabled,
[data-theme="dark"] .feature-case-card.is-master-disabled {
    background-color: rgba(15, 23, 42, 0.65);
    border-color: #1e293b !important;
}

.feature-case-label {
    color: #0f172a;
    line-height: 1.25;
}

[data-theme="dark"] .feature-case-label {
    color: #f8fafc;
}

.feature-case-desc {
    line-height: 1.3;
}

.feature-preview-pill {
    font-size: 0.68rem;
    font-weight: 700;
    line-height: 1.15;
    padding: 2px 7px;
    border-radius: 999px;
}

.feature-preview-pill--amber {
    background-color: rgba(245, 158, 11, 0.14);
    color: #b45309;
    border: 1px solid rgba(245, 158, 11, 0.35);
}

[data-theme="dark"] .feature-preview-pill--amber {
    background-color: rgba(245, 158, 11, 0.18);
    color: #fcd34d;
    border-color: rgba(245, 158, 11, 0.42);
}

.feature-preview-pill--emerald {
    background-color: rgba(16, 185, 129, 0.14);
    color: #047857;
    border: 1px solid rgba(16, 185, 129, 0.35);
}

[data-theme="dark"] .feature-preview-pill--emerald {
    background-color: rgba(16, 185, 129, 0.18);
    color: #6ee7b7;
    border-color: rgba(16, 185, 129, 0.42);
}

.feature-preview-pill--rose {
    background-color: rgba(244, 63, 94, 0.14);
    color: #be123c;
    border: 1px solid rgba(244, 63, 94, 0.35);
}

[data-theme="dark"] .feature-preview-pill--rose {
    background-color: rgba(244, 63, 94, 0.2);
    color: #fda4af;
    border-color: rgba(244, 63, 94, 0.45);
}

.feature-preview-pill--sky {
    background-color: rgba(2, 132, 199, 0.14);
    color: #0369a1;
    border: 1px solid rgba(2, 132, 199, 0.35);
}

[data-theme="dark"] .feature-preview-pill--sky {
    background-color: rgba(56, 189, 248, 0.18);
    color: #7dd3fc;
    border-color: rgba(56, 189, 248, 0.42);
}
</style>
